import { STAFF_ROLE_WORDS, STAFF_ROLES, type StaffRole } from "@atzmai/schema";
import { useState } from "react";
import { ago, api, errorText } from "../../lib/api.ts";
import { useApi } from "../../lib/useApi.ts";
import { Loading, ScreenHead, Table, useStaff } from "./ConsoleLayout.tsx";

interface Member { email: string; role: StaffRole; added_by: string; added_at: string }

const memberPath = (email: string) => `/api/console/team/${encodeURIComponent(email)}`;

export function Team() {
  const me = useStaff();
  const { data, error, reload } = useApi<{ staff: Member[] }>("/api/console/team");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<StaffRole>("viewer");
  const [msg, setMsg] = useState<string | null>(null);
  // Confirmation lives in the page: the first click arms, the second acts.
  const [armed, setArmed] = useState<string | null>(null);

  async function change(done: string, call: () => Promise<unknown>) {
    setArmed(null);
    try {
      await call();
      setMsg(done);
      return true;
    } catch (e) {
      setMsg(errorText(e));
      return false;
    } finally {
      await reload();
    }
  }

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const ok = await change(`${email} added as ${STAFF_ROLE_WORDS[role].label.toLowerCase()}. They also need to be allowed in the Cloudflare Access policy.`,
      () => api("/api/console/team", { json: { email, role } }));
    if (ok) setEmail("");
  }

  function remove(m: Member) {
    if (armed !== m.email) { setArmed(m.email); return; }
    void change(`${m.email} removed. They lose access straight away.`, () => api(memberPath(m.email), { method: "DELETE" }));
  }

  if (!data) return <><ScreenHead label="Team" /><Loading error={error} /></>;
  return (
    <div className="flex flex-col gap-6">
      <ScreenHead label="Team" />
      {msg && <p role="status" className="rounded-xl bg-glass px-4 py-2.5 text-[15px] text-ink">{msg}</p>}
      <Table>
        <thead><tr><th>Email</th><th>Level</th><th>Added</th><th /></tr></thead>
        <tbody>
          {data.staff.map((m) => (
            <tr key={m.email}>
              <td className="[overflow-wrap:anywhere]">{m.email}{m.email === me.email && <span className="text-ink-3"> (you)</span>}</td>
              <td>
                {m.email === me.email ? STAFF_ROLE_WORDS[m.role].label : (
                  <select className="input min-h-9 py-1 text-[15px]" aria-label={`Level for ${m.email}`} value={m.role}
                    onChange={(e) => { const to = e.target.value as StaffRole; void change(`${m.email} is now ${STAFF_ROLE_WORDS[to].label.toLowerCase()}.`, () => api(memberPath(m.email), { method: "PATCH", json: { role: to } })); }}>
                    {STAFF_ROLES.map((r) => <option key={r} value={r}>{STAFF_ROLE_WORDS[r].label}</option>)}
                  </select>
                )}
              </td>
              <td>{ago(m.added_at)}<div className="text-[13px] text-ink-3">by {m.added_by}</div></td>
              <td className="text-right">{m.email !== me.email && <button className="btn btn-danger" onClick={() => remove(m)}>{armed === m.email ? "Confirm remove" : "Remove"}</button>}</td>
            </tr>
          ))}
        </tbody>
      </Table>

      <form onSubmit={(e) => void add(e)} className="card flex flex-col gap-4 p-5">
        <h2 className="text-[17px]">Add someone</h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex min-w-64 flex-1 flex-col gap-1.5 text-[15px]">Email they sign in with
            <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5 text-[15px]">Level
            <select className="input" value={role} onChange={(e) => setRole(e.target.value as StaffRole)}>
              {STAFF_ROLES.map((r) => <option key={r} value={r}>{STAFF_ROLE_WORDS[r].label}</option>)}
            </select>
          </label>
          <button className="btn btn-primary">Add</button>
        </div>
        <p className="text-[15px] text-ink-2">{STAFF_ROLE_WORDS[role].can}.</p>
        <p className="text-[13px] text-ink-3">Getting in takes two things: this list, and being allowed by the Cloudflare Access policy, which is changed in the Cloudflare dashboard. You can't change your own access here, so there's always a super admin.</p>
      </form>
    </div>
  );
}
